import {Address, BasisType, BasisTypeName, BookableSchedules, BookableSchedulesDTO, Period, Profile_ID, RRDate} from "@raskrask-dk/raskrask-utils";
import {Mutex} from "async-mutex";
import { IBasisTypeProfileId, IRepository} from "..";
import {BookableAssembler} from "../assembler";
import type {IBookableAssembler, ISchedules} from "../assembler/bookable.interface";
import {RepositoryFake} from "../repository";
import type {IBookingService} from "./service.types";

export class BookingService implements IBookingService {
  private loadedPeriods: Period[] = [];
  private profileIds: [id: number, profileIds: number[]][] = [];
  private isLoading: Mutex = new Mutex();
  private cacheCounter = 0; // When it changes, the repository will not use cache as the input has been changed.

  constructor(
    public readonly repo: IRepository = new RepositoryFake(),
    private assembler: IBookableAssembler = new BookableAssembler(),
    private customerRequest: BookableSchedules | "NON" = "NON"
  ) {
    if (customerRequest !== "NON") {
      this.loadedPeriods.push(customerRequest.period);
    }
  }

  invalidateCache = (): void => {
    this.cacheCounter = this.cacheCounter + 1;
  };

  get schedules(): ISchedules {
    return this.assembler.schedules();
  }

  async getOfferedBasisTypes(
    address: Address,
    startDate: string,
    numberOfDaysToCheck: number,
    profileIds: Profile_ID[] = []
  ): Promise<IBasisTypeProfileId[]> {
    const getOfferedBTFromRepo = await this.repo.getOfferedBasisTypes(address, startDate, numberOfDaysToCheck);

    const basisTypeProfileId: IBasisTypeProfileId[] = getOfferedBTFromRepo.map((x) => {
      const allowedProfiles = x.profileIds.filter((pB) => {
        if (profileIds.length === 0) return true;
        return profileIds.includes(pB);
      });

      return {id: x.id, name: x.name, available: x.available, customerTypes: x.customerTypes, filters: {profileIds: allowedProfiles}};
    });

    type basisToProfileId = [id: number, profileIds: number[]];
    const basisToProfileIds = basisTypeProfileId.reduce((accum, curr) => {
      const ids = curr.filters.profileIds;
      const row: basisToProfileId = [curr.id, ids];
      const returns = [...accum, row];
      return returns;
    }, [] as basisToProfileId[]);

    this.profileIds = basisToProfileIds;

    this.loadSchedules(mapToBookableSchedules(address, profileIds));
    return basisTypeProfileId;
  }

  async loadSchedules(bookableSchedule: BookableSchedules): Promise<ISchedules> {
    if (bookableSchedule.basisType.id !== "1") {
      return await this._loadSchedules(bookableSchedule, this.cacheCounter);
    }
    const load = await this.isLoading.runExclusive(async () => {
      return await this._loadSchedules(bookableSchedule, this.cacheCounter);
    });
    return load;
  }

  private async _loadSchedules(bookableSchedule: BookableSchedules, cacheInvalid: number): Promise<ISchedules> {
    this._updateCustomerRequest({type: "inital", bookableSchedule});
    const period = this.loadedPeriods[this.loadedPeriods.length - 1];
    const schedulesDTO = await this.repo.getSchedules(
      mapBookableSchedulesDTO(bookableSchedule, period, bookableSchedule.filters?.profileIds ?? []),
      cacheInvalid
    );
    const schedules = this.assembler.createSchedules(
      schedulesDTO,
      new BasisType(bookableSchedule.basisType.name as BasisTypeName, parseInt(bookableSchedule.basisType.id))
    );
    return schedules;
  }
  async loadMore(days: number, basisType: BasisType): Promise<ISchedules> {
    if (this.customerRequest === "NON") return this.assembler.schedules();
    this._updateCustomerRequest({type: "loadmore", days});

    const period = this.loadedPeriods[this.loadedPeriods.length - 1];
    const schedulesDTO = await this.repo.getSchedules(mapBookableSchedulesDTO(this.customerRequest, period, []), this.cacheCounter);
    const schedules = this.assembler.addSchedules(schedulesDTO, basisType);
    return schedules;
  }

  private _updateCustomerRequest(event: Event): void {
    if (event.type === "inital") {
      this.customerRequest = event.bookableSchedule;
      const newPeriod = this.customerRequest.period.clone();
      this.loadedPeriods.push(newPeriod);
    }
    if (event.type === "loadmore" && this.customerRequest !== "NON") {
      const lastPeriod = this.loadedPeriods[this.loadedPeriods.length - 1];
      const newPeriod = new Period(lastPeriod.to, lastPeriod.to.clone().addDays(event.days));
      this.loadedPeriods.push(newPeriod);
    }
  }
}

type Event = {type: "inital"; bookableSchedule: BookableSchedules} | {type: "loadmore"; days: number};

const mapBookableSchedulesDTO = (bookableSchedule: BookableSchedules, period: Period, arr: number[]): BookableSchedulesDTO => {
  const periodDTO = {
    start: period.from.toString("yyyy-MM-dd HH:mm"),
    days: period.duration().toString("d"),
  };
  return {
    ...bookableSchedule,
    period: periodDTO,
    filters: {
      profileIds: arr,
    },
  };
};

const mapToBookableSchedules = (address: Address, profileIds: number[]): BookableSchedules => {
  const bookableSchedule: BookableSchedules = {
    customer: {
      customerType: "Private",
      address: {geoLat: address.geoLat, geoLng: address.geoLng, zipCode: address.zipCode.toString(), address: address.address, floor: address.floor},
    },
    basisType: {id: "1", name: "Massage", shortestDuration: "0", medium: "InPerson"},
    period: new Period(new RRDate(), new RRDate().addDays(28).set({hour: 0, minute: 0, second: 0})),
    filters: {
      profileIds: profileIds,
    },
  };
  return bookableSchedule;
};
